import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, StatusPill } from '~/components/ui';
import { JOB_DATA_FLOW, type AreaFlow, type AreaKind } from '~/data/jobDataFlow';

export const Route = createFileRoute('/_app/admin/data-flow')({
  component: DataFlowPage,
});

// ─── Origin classification (where in the job/customer the data lives) ────────
type Entity = 'job' | 'customer' | 'claim' | 'people' | 'dates' | 'compliance' | 'derived';

const ENTITY_META: Record<Entity, { label: string; dot: string; chip: string }> = {
  job:        { label: 'Job',        dot: 'bg-[#2563eb]', chip: 'bg-[#2563eb]/10 text-[#1e40af]' },
  customer:   { label: 'Customer',   dot: 'bg-[#059669]', chip: 'bg-[#059669]/10 text-[#047857]' },
  claim:      { label: 'Claim',      dot: 'bg-[#d97706]', chip: 'bg-[#d97706]/10 text-[#b45309]' },
  people:     { label: 'People',     dot: 'bg-[#7c3aed]', chip: 'bg-[#7c3aed]/10 text-[#6d28d9]' },
  dates:      { label: 'Dates',      dot: 'bg-[#0891b2]', chip: 'bg-[#0891b2]/10 text-[#0e7490]' },
  compliance: { label: 'Compliance', dot: 'bg-[#dc2626]', chip: 'bg-[#dc2626]/10 text-[#b91c1c]' },
  derived:    { label: 'Derived',    dot: 'bg-[#6b7280]', chip: 'bg-[#6b7280]/10 text-[#4b5563]' },
};

/** Best-effort: classify a read by which entity its data ultimately comes from. */
function classify(source: string, origin: string): Entity {
  const s = `${source} ${origin}`.toLowerCase();
  if (/(generated|derived-in-view|correlated subselect|aggregat|coalesce\()/.test(s)) {
    // a view-derived value, unless it's clearly a passthrough of a base column
    if (/customers\./.test(s)) return 'customer';
    if (/insurance_carriers|claims\./.test(s)) return 'claim';
    if (/compliance_tasks/.test(s)) return 'compliance';
    if (/job_dates/.test(s)) return 'dates';
    if (/profiles|external_contacts|job_participants/.test(s)) return 'people';
    return 'derived';
  }
  if (/compliance/.test(s)) return 'compliance';
  if (/job_dates/.test(s)) return 'dates';
  if (/insurance_carriers|claims/.test(s)) return 'claim';
  if (/participants|profiles|external_contacts/.test(s)) return 'people';
  if (/customers/.test(s)) return 'customer';
  return 'job';
}

const KIND_ORDER: AreaKind[] = ['foundation', 'header', 'card', 'tab', 'stub'];
const KIND_LABEL: Record<AreaKind, string> = {
  foundation: 'Foundation', header: 'Header', card: 'Cards', tab: 'Tabs', stub: 'Stubs',
};

function DataFlowPage() {
  const [selectedArea, setSelectedArea] = useState(JOB_DATA_FLOW[0]?.area ?? '');
  const selected = JOB_DATA_FLOW.find((a) => a.area === selectedArea) ?? JOB_DATA_FLOW[0];

  const groups = useMemo(() => {
    return KIND_ORDER.map((kind) => ({
      kind,
      items: JOB_DATA_FLOW.filter((a) => a.kind === kind),
    })).filter((g) => g.items.length > 0);
  }, []);

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto w-full">
      <div className="mb-5">
        <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Job Data Flow Map</h1>
        <p className="text-sm text-[var(--color-ink-soft)] mt-1 max-w-3xl">
          Pick an area of the job page on the left. For each one this shows, in plain English: what
          information it <strong>shows you</strong> (and where that information comes from), what it
          <strong> figures out</strong> on its own (totals, counts, due dates), and what
          <strong> changes</strong> when you use it. It&rsquo;s a way to double-check that every part of a
          job is pulling the right numbers.
        </p>
      </div>

      {/* How the page loads — plain-English overview */}
      <Card className="mb-5">
        <CardBody className="text-sm text-[var(--color-ink-soft)]">
          <span className="font-semibold text-[var(--color-ink)]">How a job opens:</span>{' '}
          When you open a job, the app loads everything about it at once — the job itself plus the
          customer, the insurance claim, the people assigned to it, the key dates, and its compliance
          tasks. Each card and tab on the job page then shows a slice of that information. Any time you
          change something, the change is saved safely on the server (not directly from your browser),
          and the rest of the page refreshes to match.
        </CardBody>
      </Card>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-[var(--color-ink-soft)]">
        <span className="font-semibold text-[var(--color-ink)]">Where data lives:</span>
        {(Object.keys(ENTITY_META) as Entity[]).map((e) => (
          <span key={e} className="inline-flex items-center gap-1.5">
            <span className={`size-2.5 rounded-full ${ENTITY_META[e].dot}`} />
            {ENTITY_META[e].label}
          </span>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        {/* Left rail */}
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.kind}>
              <div className="text-xs uppercase tracking-wide font-semibold text-[var(--color-ink-muted)] mb-1.5 px-1">
                {KIND_LABEL[g.kind]}
              </div>
              <div className="space-y-1">
                {g.items.map((a) => {
                  const active = a.area === selected.area;
                  return (
                    <button
                      key={a.area}
                      onClick={() => setSelectedArea(a.area)}
                      className={
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ' +
                        (active
                          ? 'bg-[var(--color-navy)] text-white font-semibold'
                          : 'hover:bg-[var(--color-surface-1)] text-[var(--color-ink)]')
                      }
                    >
                      {a.area}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        {selected && <AreaDetail area={selected} />}
      </div>
    </div>
  );
}

function AreaDetail({ area }: { area: AreaFlow }) {
  return (
    <div className="space-y-5 min-w-0">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-2xl font-bold text-[var(--color-navy)]">{area.area}</h2>
          <StatusPill kind="muted">{area.kind}</StatusPill>
        </div>
        <p className="text-sm text-[var(--color-ink-soft)] mt-1">{area.summary}</p>
        <code className="inline-block mt-2 text-xs font-mono text-[var(--color-ink-muted)] bg-[var(--color-surface-1)] rounded px-2 py-0.5">
          {area.file}
        </code>
      </div>

      {/* Reads */}
      <Section title="What it shows" count={area.reads.length} accent="#2563eb">
        {area.reads.length === 0 ? (
          <Empty>Doesn&rsquo;t show any stored information.</Empty>
        ) : (
          <div className="space-y-3">
            {area.reads.map((r, i) => {
              const e = classify(r.source, r.origin);
              const meta = ENTITY_META[e];
              return (
                <div key={i} className="rounded-lg border border-[var(--color-line)] p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`size-2.5 rounded-full ${meta.dot}`} title={meta.label} />
                    <span className="font-mono text-sm font-semibold">{r.source}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${meta.chip}`}>{meta.label}</span>
                  </div>
                  <div className="mt-1.5 text-xs text-[var(--color-ink-muted)]">
                    <span className="font-semibold">how it&rsquo;s loaded:</span> <span className="font-mono">{r.via}</span>
                  </div>
                  {r.fields.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.fields.map((f) => (
                        <span key={f} className="text-[11px] font-mono bg-[var(--color-surface-1)] rounded px-1.5 py-0.5 text-[var(--color-ink-soft)]">{f}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-[var(--color-ink-soft)] leading-relaxed">
                    <span className="font-semibold text-[var(--color-ink)]">Where it comes from:</span> {r.origin}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Calculations */}
      <Section title="What it figures out" count={area.calculations.length} accent="#7c3aed">
        {area.calculations.length === 0 ? (
          <Empty>Nothing is calculated here — numbers are shown exactly as they&rsquo;re saved.</Empty>
        ) : (
          <div className="space-y-3">
            {area.calculations.map((c, i) => (
              <div key={i} className="rounded-lg border border-[var(--color-line)] p-3">
                <div className="text-sm font-semibold text-[var(--color-ink)]">{c.label}</div>
                <pre className="mt-1.5 text-xs font-mono whitespace-pre-wrap break-words bg-[var(--color-surface-1)] rounded p-2 text-[var(--color-ink-soft)]">{c.formula}</pre>
                {c.inputs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.inputs.map((inp) => (
                      <span key={inp} className="text-[11px] font-mono bg-[#7c3aed]/10 text-[#6d28d9] rounded px-1.5 py-0.5">{inp}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Writes */}
      <Section title="What it changes" count={area.writes.length} accent="#dc2626">
        {area.writes.length === 0 ? (
          <Empty>View only — nothing here changes any data.</Empty>
        ) : (
          <div className="space-y-3">
            {area.writes.map((w, i) => {
              const readOnly = /^none|read-only/i.test(w.action);
              return (
                <div key={i} className="rounded-lg border border-[var(--color-line)] p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill kind={readOnly ? 'muted' : 'info'}>{readOnly ? 'view only' : 'saves changes'}</StatusPill>
                    <span className="text-sm font-semibold text-[var(--color-ink)]">{w.action}</span>
                  </div>
                  {!readOnly && (
                    <div className="mt-1.5 text-xs text-[var(--color-ink-muted)]">
                      <span className="font-semibold">how it&rsquo;s saved:</span> <span className="font-mono break-words">{w.via}</span>
                    </div>
                  )}
                  {w.tables.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1 items-center">
                      <span className="text-[11px] text-[var(--color-ink-muted)]">updates:</span>
                      {w.tables.map((t) => (
                        <span key={t} className="text-[11px] font-mono bg-[#dc2626]/10 text-[#b91c1c] rounded px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                  {w.sideEffects && w.sideEffects !== 'none' && (
                    <div className="mt-2 text-xs text-[var(--color-ink-soft)] leading-relaxed">
                      <span className="font-semibold text-[var(--color-ink)]">Also happens:</span> {w.sideEffects}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Notes */}
      {area.notes && (
        <Section title="Notes & gaps" accent="#6b7280">
          <p className="text-xs text-[var(--color-ink-soft)] leading-relaxed whitespace-pre-wrap">{area.notes}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, accent, children }: {
  title: string; count?: number; accent: string; children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <span className="inline-flex items-center gap-2">
          <span className="size-2.5 rounded-full" style={{ background: accent }} />
          {title}{count != null && <span className="text-[var(--color-ink-muted)] font-normal">({count})</span>}
        </span>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-[var(--color-ink-muted)]">{children}</div>;
}
